import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { MotifTable } from '@ey-xd/motif-react';
import CustomHeaderTemplate from '../shared/customHeaders/CustomHeaderTemplate.js';
import SignOffReitCheckboxHeaderRenderer from './customRenderers/SignOffReitCheckboxHeaderRenderer.js';
import SignOffReitCheckboxCellRenderer from './customRenderers/SignOffReitCheckboxCellRenderer.js';
import CustomReitToolTip from './customRenderers/CustomReitToolTip.js';
import PropTypes from 'prop-types';
import CustomPagination from '../../common/MotifPagination';

const SignOffTable = ({
  bulkProcessManagementData,
  gridColumnState,
  handleGridChanges,
  handleGridColumnStateChange,
}) => {
  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);
  const rowDataRef = useRef({});
  rowDataRef.current = rowData;

  const hasData =
    bulkProcessManagementData && bulkProcessManagementData.length > 0;

  const onHeaderCheckboxChange = useCallback((isHeaderCheckboxSelected) => {
    let updatedData = rowDataRef.current;
    if (gridRef && gridRef.current && gridRef.current.api) {
      // Select checkbox for filtered rows only
      gridRef.current.api.forEachNodeAfterFilter((rowNode) => {
        // Invert the selection status for each row
        if (rowNode.level === 0) {
          updatedData = rowDataRef.current.map((newUd) => {
            if (newUd.isReitAvailableForSelection) {
              return {
                ...newUd,
                reitSelection: !isHeaderCheckboxSelected,
              };
            }
            return newUd;
          });
        }
      });

      setRowData(updatedData);
      gridRef.current.api.refreshHeader();
    }
  }, []);

  const onCellCheckboxChange = useCallback((reitId, rowNode) => {
    const updatedData = rowDataRef.current.map((newUd) => {
      if (newUd.reitid === reitId) {
        return {
          ...newUd,
          reitSelection: !newUd.reitSelection,
        };
      }
      return newUd;
    });

    setRowData(updatedData);
    gridRef.current.api.refreshHeader();
  }, []);

  // Default column settings
  const defaultColDef = useMemo(() => {
    return {
      flex: 1,
      minWidth: 65,
      filter: true,
      resizable: true,
      sortable: true,
      wrapText: true,
      autoHeight: true,
    };
  }, []);

  const columnDefs = useMemo(() => {
    return [
      {
        field: 'reitSelection',
        headerName: '',
        suppressMenu: true,
        suppressHeaderFilterButton: true,
        headerComponent: SignOffReitCheckboxHeaderRenderer,
        headerComponentParams: {
          onHeaderCheckboxChange: onHeaderCheckboxChange,
        },
        cellRenderer: SignOffReitCheckboxCellRenderer,
        cellRendererParams: { onCellCheckboxChange: onCellCheckboxChange },
        minWidth: 100,
        maxWidth: 150,
        spanHeaderHeight: true,
      },
      {
        field: 'reitTrackingID',
        headerName: 'REITSuite Entity ID for REIT',
        sortable: true,
        showRowGroup: false,
        rowGroup: false,
        minWidth: 150,
        valueGetter: (params) => {
          return params.data && params.data.reitTrackingID;
        },
        headerComponentParams: {
          template: CustomHeaderTemplate('REITSuiteEntityIDForREIT'),
        },
      },
      {
        field: 'reitName',
        headerName: 'REIT Name',
        sortable: true,
        showRowGroup: false,
        rowGroup: false,
        minWidth: 500,
        maxWidth: 700,
        valueGetter: (params) => {
          return params.data && params.data.reitName;
        },
        headerComponentParams: { template: CustomHeaderTemplate('ReitName') },
        cellRenderer: CustomReitToolTip,
      },
      {
        field: 'signOffStatus',
        headerName: 'Sign Off Status',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 100,
        maxWidth: 700,
        valueGetter: (params) => {
          return params.data && params.data.signOffStatus;
        },
        headerComponentParams: {
          template: CustomHeaderTemplate('SignOffStatus'),
        },
        spanHeaderHeight: true,
      },
    ];
  }, [onHeaderCheckboxChange, onCellCheckboxChange]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);

  //Grid Global Listner
  const gridGlobalListner = useCallback(
    function (type, event) {
      if (type.indexOf('columnVisible') >= 0) {
        handleGridColumnsChanged(event);
      }

      // Bind handleFilterChanged event to filterChanged and rowDataUpdated events
      if (type.indexOf('filterChanged') >= 0) {
        setTimeout(function () {
          handleFilterChanged(event);
        }, 0);
      }

      if (type.indexOf('rowDataUpdated') >= 0) {
        setTimeout(function () {
          handleFilterChanged(event);
        }, 0);
      }
    },
    [handleGridColumnsChanged, handleFilterChanged]
  );

  //Handle Grid Ready event
  const handleGridReady = useCallback(
    (event) => {
      //Remove event for column state change
      event.api.removeEventListener(gridGlobalListner);

      //Add event for column state change
      event.api.addGlobalListener(gridGlobalListner);

      //Apply column state
      var columnState = localStorage.getItem('REITSuiteSignOffColumnState');
      if (columnState) {
        gridRef.current.columnApi.applyColumnState({
          state: JSON.parse(columnState),
          applyOrder: true,
        });
      }

      // Set the page size and total pages on the first load
      handleItemsPerPageChange(100); // Setting it 100 to make sure the default number of items on load is always set to 100
    },
    [gridGlobalListner, handleItemsPerPageChange]
  );

  // Handle OnFilterChanged event
  const handleFilterChanged = useCallback(
    (event) => {
      if (gridRef && gridRef.current && gridRef.current.api) {
        let calcTotalPages = gridRef.current.api.paginationGetTotalPages();
        setTotalPages(calcTotalPages);

        if (currentPage > calcTotalPages) {
          setCurrentPage(
            calcTotalPages === 0 ? calcTotalPages : calcTotalPages
          );
        }

        const gridCurrentPage =
          gridRef.current.api.paginationGetCurrentPage() + 1; // Adding 1 since it returns zero-based index
        if (currentPage < gridCurrentPage && calcTotalPages !== 0) {
          setCurrentPage(gridCurrentPage);
        }

        if (currentPage <= 1 && gridCurrentPage === 1 && calcTotalPages !== 0) {
          setCurrentPage(gridCurrentPage);
        }
      }
    },
    [currentPage]
  );

  //Handle First Data Renderered event
  const handleFirstDataRendered = useCallback((event) => {
    gridRef.current.columnApi.autoSizeAllColumns();
  }, []);

  //Handle Grid Columns changed event
  const handleGridColumnsChanged = useCallback(
    (event) => {
      var currentColumnState = gridRef.current.columnApi.getColumnState();
      var localStorageColumnState = localStorage.getItem(
        'REITSuiteSignOffColumnState'
      );

      // If the column state details are not exists in local storage then save the changed Column state data
      if (
        currentColumnState &&
        currentColumnState.filter((col) => col.hide == true)?.length > 0 &&
        !localStorageColumnState
      ) {
        saveUserPreferenceDetails();
      } else if (localStorageColumnState) {
        // If the column state details are exists in local storage then save the changed Column state data only if difference is found
        var columnsStateArray = JSON.parse(localStorageColumnState);
        if (
          columnsStateArray &&
          columnsStateArray.length > 0 &&
          currentColumnState &&
          currentColumnState.length > 0 &&
          columnsStateArray.filter((col) => col.hide == true)?.length !=
            currentColumnState.filter((col) => col.hide == true)?.length
        ) {
          //If column state mismatch found then only save column state data to avoid saving same state
          saveUserPreferenceDetails();
        }
      }

      //Trigger Grid Column state change (of parent) to sync column state data
      handleGridColumnStateChange(currentColumnState);

      gridRef &&
        gridRef.current &&
        gridRef.current.columnApi &&
        gridRef.current.columnApi.autoSizeAllColumns();
    },
    [handleGridColumnStateChange]
  );

  //Save User Preference details (Column state)
  const saveUserPreferenceDetails = useCallback(() => {
    var columnState = gridRef.current.columnApi.getColumnState();
    if (columnState) {
      localStorage.setItem(
        'REITSuiteSignOffColumnState',
        JSON.stringify(columnState)
      );
    }
  }, []);

  //Handle Grid Items per Page change event
  const handleItemsPerPageChange = useCallback((val) => {
    gridRef.current.api.paginationGoToFirstPage();
    gridRef.current.api.setGridOption('paginationPageSize', Number(val));
    setCurrentPage(1);
    setItemsPerPage(val);
    // Get total pages from Grid's default pagination control and apply it to custom pagination control
    if (gridRef && gridRef.current && gridRef.current.api) {
      let calcTotalPages = gridRef.current.api.paginationGetTotalPages();
      setTotalPages(calcTotalPages);
      //If total page is zero then set the current page as zero
      if (calcTotalPages == 0) {
        setCurrentPage(calcTotalPages);
      }
    }
  }, []);

  //Handle Grid Page change event
  const handlePageChange = useCallback((val) => {
    setCurrentPage(val);
    gridRef.current.api.paginationGoToPage(val - 1);
  }, []);

  // Memoize handleGridChanges to ensure a stable reference
  const memoizedHandleGridChanges = useCallback(() => {
    if (gridRef && gridRef.current && gridRef.current.api) {
      handleGridChanges(rowData, gridRef.current.api);
    }
  }, [rowData, handleGridChanges]);

  useEffect(() => {
    memoizedHandleGridChanges();
    if (rowData.length > 0 && gridRef?.current?.api !== undefined) {
      handleGridChanges(rowData, gridRef.current.api);
    }
  }, [memoizedHandleGridChanges, rowData]);

  useEffect(() => {
    handleGridChanges(
      rowData,
      gridRef && gridRef.current ? gridRef.current.api : null
    );
    if (rowData && rowData.length > 0 && gridRef?.current?.api !== undefined) {
      let calcTotalPages =
        gridRef &&
        gridRef.current &&
        gridRef.current.api &&
        gridRef.current.api.paginationGetTotalPages();
      setTotalPages(calcTotalPages);
    }
  }, [rowData]);

  useEffect(() => {
    setRowData(bulkProcessManagementData);

    if (gridRef && gridRef.current && gridRef.current.api) {
      gridRef.current.api.redrawRows();
      gridRef.current.api.refreshHeader();
      // Get total pages from Grid's default pagination control and apply it to custom pagination control
      let calcTotalPages = gridRef.current.api.paginationGetTotalPages();
      setTotalPages(calcTotalPages);
      if (calcTotalPages > 0) {
        setCurrentPage(gridRef.current.api.paginationGetCurrentPage() + 1);
      }
    }
  }, [bulkProcessManagementData]);

  useEffect(() => {
    //Reset Grid to default column state
    //When Grid Column state changes due to Reset Column State action then Reset Grid to default column state
    var localStorageColumnState = localStorage.getItem(
      'REITSuiteSignOffColumnState'
    );
    if (
      !localStorageColumnState &&
      gridRef &&
      gridRef.current &&
      gridRef.current.api
    ) {
      gridRef.current.api.setGridOption('columnDefs', []);
      gridRef.current.api.setGridOption('columnDefs', columnDefs);
    }
  }, [gridColumnState]);

  const rowHeight = 76;
  const headerHeight = 45;
  const totalHeight = 5 * rowHeight + headerHeight; // set 5 rows as default

  return (
    <React.Fragment>
      <div className="row mb-10 mt-3">
        {bulkProcessManagementData && bulkProcessManagementData.length > 0 && (
          <div className="col mt-3 bulk-processmanagement-grid">
            <div
              className="ag-theme-quartz motif-table table-bd-custom-height-width-sign-off"
              data-testid="grdSignOffData"
            >
              <MotifTable
                zebra={true}
                compact={true}
                rowData={rowData}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                accentedSort={true}
                rowSelection="multiple"
                suppressRowClickSelection={true}
                onGridReady={handleGridReady}
                onFirstDataRendered={handleFirstDataRendered}
                ref={gridRef}
                pagination={true}
                suppressPaginationPanel={true}
                paginationPageSize={100}
                groupDisplayType={'custom'}
                groupSelectsChildren={true}
                groupDefaultExpanded={0}
                onFilterChanged={handleFilterChanged}
              />
              <div className="mt-3">
                <span className="right mb-3">
                  <CustomPagination
                    currentPage={currentPage}
                    handlePageChange={handlePageChange}
                    totalPages={totalPages}
                    handleItemsPerPageChange={handleItemsPerPageChange}
                    gridRef={gridRef}
                    setCurrentPage={setCurrentPage}
                    setItemsPerPage={setItemsPerPage}
                    setTotalPages={setTotalPages}
                  />
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

SignOffTable.propTypes = {
  bulkProcessManagementData: PropTypes.array.isRequired,
  gridColumnState: PropTypes.array.isRequired,
  handleGridChanges: PropTypes.func,
};

export default SignOffTable;
